/**
 * useHelpManifest
 * Busca o manifest da Central de Ajuda em /tutoriais/manifest.json
 */

import { useEffect, useState } from 'react';

export interface HelpScreen {
  id: string;
  title: string;
  /** Caminho relativo a /tutoriais/, ex.: "pt-BR/appointments/lista.md" */
  file: string;
}

export interface HelpEntity {
  id: string;
  label: string;
  screens: HelpScreen[];
}

export interface HelpManifest {
  defaultLanguage: string;
  entities: HelpEntity[];
}

interface UseHelpManifestResult {
  manifest: HelpManifest | null;
  loading: boolean;
  error: string | null;
}

const MANIFEST_URL = '/tutoriais/manifest.json';

/**
 * Os tutoriais mudam sem trocar de nome — um print novo entra no lugar do antigo. Sem isto, o
 * navegador de quem já abriu a ajuda seguia mostrando o texto e o print de antes do deploy (a
 * marca MEVMED, em out/2026). O texto é sempre conferido com o servidor (`no-cache`) e cada
 * build carimba a própria versão no endereço dos prints, que assim nunca vêm de um cache velho.
 * Fora do Vite (testes) a constante não existe e o endereço fica como está.
 */
const VERSAO_DO_BUILD = typeof __BUILD_TIME__ === 'string' ? __BUILD_TIME__ : '';

export function comVersaoDoBuild(url: string): string {
  if (!VERSAO_DO_BUILD || !url.startsWith('/tutoriais/')) return url;
  return `${url}${url.includes('?') ? '&' : '?'}v=${encodeURIComponent(VERSAO_DO_BUILD)}`;
}

export function buscarTutorial(url: string): Promise<Response> {
  return fetch(comVersaoDoBuild(url), { cache: 'no-cache' });
}

function isHelpScreen(value: unknown): value is HelpScreen {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.id === 'string' &&
    typeof candidate.title === 'string' &&
    typeof candidate.file === 'string'
  );
}

function isHelpEntity(value: unknown): value is HelpEntity {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.id === 'string' &&
    typeof candidate.label === 'string' &&
    Array.isArray(candidate.screens) &&
    candidate.screens.every(isHelpScreen)
  );
}

function isHelpManifest(value: unknown): value is HelpManifest {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.defaultLanguage === 'string' &&
    Array.isArray(candidate.entities) &&
    candidate.entities.every(isHelpEntity)
  );
}

export function useHelpManifest(): UseHelpManifestResult {
  const [manifest, setManifest] = useState<HelpManifest | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function loadManifest() {
      setLoading(true);
      setError(null);
      try {
        const response = await buscarTutorial(MANIFEST_URL);
        if (!response.ok) {
          throw new Error(`Falha ao carregar manifest (HTTP ${response.status})`);
        }
        const data: unknown = await response.json();
        if (!isHelpManifest(data)) {
          throw new Error('Formato de manifest inválido');
        }
        if (!cancelled) {
          setManifest(data);
        }
      } catch (err: unknown) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Erro ao carregar a Central de Ajuda');
          setManifest(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    void loadManifest();

    return () => {
      cancelled = true;
    };
  }, []);

  return { manifest, loading, error };
}
